/**
 * 득점 주체 식별자 — 공 색 하나에 대응한다.
 *
 * 팀전은 0/1 두 개, **개인전은 0..N-1**로 사람 수만큼 있다. 그래서 2로 고정하지
 * 않는다. "팀"이라는 이름은 팀전에서 온 것이고, 개인전에서는 한 사람이 곧 한 팀이다.
 */
export type TeamId = number;

/** 이 매치에 존재하는 득점 주체 목록. */
export function teamIds(count: number): TeamId[] {
  return Array.from({ length: count }, (_, i) => i);
}

/** @deprecated 2팀 전용. 개인전에서는 `nextTeam`을 쓸 것. */
export function otherTeam(team: TeamId): TeamId {
  return team === 0 ? 1 : 0;
}

/** 차례가 넘어갈 다음 주체. 팀전(2)이면 상대, 개인전이면 다음 사람. */
export function nextTeam(team: TeamId, count: number): TeamId {
  return (team + 1) % count;
}

/**
 * 대전에서 팀이 서는 자리. 실물 게임처럼 **왼쪽/오른쪽이 고정**이고,
 * 라운드가 바뀌면 서로 교대한다 (docs/01-game-rules.md §2).
 *
 * 자리는 발사 가능한 x 구간을 결정한다 — 왼쪽 자리는 테이블 왼쪽 절반에서만 쏜다.
 * 빗이 가운데 정렬이라 자리를 고정하면 **자기 쪽 통로로만 뱅크샷**을 칠 수 있고,
 * 라운드마다 교대하므로 좌우 유불리가 상쇄된다.
 */
export type LaunchSide = "left" | "right";

/** Astrolabe(星盘) 위치. 1이 가장 가깝고 3이 가장 멀다. */
export type AstrolabeId = 1 | 2 | 3;

export const ASTROLABE_IDS: readonly AstrolabeId[] = [1, 2, 3];

/**
 * 칸 이름. **가운데가 A**이고 양옆으로 멀어질수록 B, C, D…
 *
 * ```
 *   7칸 →  D  C  B  A  B  C  D
 *   5칸 →     C  B  A  B  C
 *   3칸 →        B  A  B
 * ```
 *
 * 좌우 대칭이라 같은 이름이 양쪽에 하나씩 있다 (가운데 A만 유일).
 * 칸 수가 짝수면 가운데가 없으므로 중앙 두 칸이 둘 다 A가 된다.
 */
export function slotLabel(slotCount: number, slotIndex: number): string {
  const center = (slotCount - 1) / 2;
  const distance = Math.floor(Math.abs(slotIndex - center));
  return String.fromCharCode(65 + distance);
}

export type MatchFormat = "1v1" | "2v2" | "3v3" | "4v4" | "ffa3" | "ffa4" | "ffa5" | "ffa6";

export interface FormatSpec {
  format: MatchFormat;
  /** 팀당 인원 */
  teamSize: number;
  /** 방 전체 정원 */
  maxClients: number;
  /**
   * 득점 주체 수 = 공 색 수.
   *
   * 팀전은 언제나 2다(사람이 몇이든 두 색으로 나뉜다). **개인전은 사람 수와 같다** —
   * 각자가 자기 색을 갖고 자기 점수를 낸다.
   */
  teamCount: number;
  /** 개인전이면 true. 팀 개념 없이 각자 점수를 다툰다. */
  individual: boolean;
  label: string;
}

const team = (format: MatchFormat, teamSize: number, label: string): FormatSpec => ({
  format,
  teamSize,
  maxClients: teamSize * 2,
  teamCount: 2,
  individual: false,
  label,
});

const ffa = (format: MatchFormat, players: number): FormatSpec => ({
  format,
  // 개인전은 한 사람이 곧 한 팀이다.
  teamSize: 1,
  maxClients: players,
  teamCount: players,
  individual: true,
  label: `개인전 ${players}인`,
});

export const MATCH_FORMATS: Record<MatchFormat, FormatSpec> = {
  "1v1": team("1v1", 1, "1대1"),
  "2v2": team("2v2", 2, "2대2"),
  "3v3": team("3v3", 3, "3대3"),
  "4v4": team("4v4", 4, "4대4"),
  ffa3: ffa("ffa3", 3),
  ffa4: ffa("ffa4", 4),
  ffa5: ffa("ffa5", 5),
  ffa6: ffa("ffa6", 6),
};

export const ALL_FORMATS: readonly MatchFormat[] = [
  "1v1",
  "2v2",
  "3v3",
  "4v4",
  "ffa3",
  "ffa4",
  "ffa5",
  "ffa6",
];

export function isMatchFormat(value: unknown): value is MatchFormat {
  return typeof value === "string" && value in MATCH_FORMATS;
}

/**
 * 매치 진행 단계.
 *
 *   lobby → countdown → round(1) → intermission → round(2) → [overtime] → ended
 */
export type MatchPhase =
  | "lobby"
  | "countdown"
  | "round"
  | "intermission"
  | "overtime"
  | "ended";

/** 공의 상태. docs/01-game-rules.md "Ball States" 참고. */
export type BallState =
  /** 굴러가는 중 */
  | "in_play"
  /** Astrolabe 슬롯에 포획됨 → 득점 */
  | "captured"
  /** 득점 없이 정지 — 테이블에 남아 장애물이 되지만 점수 없음 */
  | "grouped"
  /** Gutter 진입 → 제거 */
  | "gutter";

/** 턴이 끝난 이유. */
export type TurnEndReason =
  /** 득점 실패 */
  | "miss"
  /** Grouping (득점 없이 정지) */
  | "grouping"
  /** 파울 */
  | "foul"
  /** 내 샷이 상대 공을 득점 위치로 밀어 넣음 → 내 점수는 인정되고 컨트롤은 상대에게 */
  | "opponent_scored"
  /** 제한시간 초과 */
  | "timeout"
  /** 공 소진 */
  | "out_of_balls"
  /** Bank Points 선택 */
  | "banked";

/** 라운드가 끝난 이유. */
export type RoundEndReason =
  /** 한 팀이 한 판(Astrolabe)을 전부 채웠다 → 만관, 즉시 종료 */
  | "grand_slam"
  /** 양 팀 공 소진 */
  | "out_of_balls"
  /** 라운드 제한시간 만료 */
  | "time_up"
  /** Final Sequence에서 Bank Points 선택 */
  | "banked";

/** 파울 종류. 온라인에서 실제로 발생 가능한 것만. */
export type FoulKind =
  /** Play Line 침범 — 발사 위치가 라인 앞 */
  | "play_line"
  /** 허용 범위를 벗어난 발사 파라미터 (조작 클라이언트 의심) */
  | "invalid_shot"
  /** 제한시간 초과 */
  | "timeout";
